var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/indent */
import { useEffect, useMemo, useState } from 'react';
import merge from 'lodash.merge';
import { createInstance } from 'i18next';
import { SovosBackdrop, SovosCircularProgress, SovosProvider, } from 'mosaic-react';
import { SovosEnvironment, } from '../types';
import PlatformContext from '../internals/PlatformContext';
import getApi from '../internals/api';
import generateSupportItemKey from '../utils/supportItemKey';
const SovosPlatformProvider = ({ assetHost, environment = SovosEnvironment.dev, children, lng, onLoadingComplete, productKey, suiteKey, theme, token, }) => {
    var _a;
    const [activeSuite, setActiveSuite] = useState();
    const [activeProduct, setActiveProduct] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [availableProducts, setAvailableProducts] = useState(undefined);
    const [supportItems, setSupportItems] = useState(undefined);
    const [i18nInstance, setI18n] = useState();
    const context = useMemo(() => ({
        activeProduct,
        availableProducts,
        environment,
        i18nInstance,
        locale: lng,
        supportItems,
        token,
    }), [
        activeProduct,
        availableProducts,
        environment,
        i18nInstance,
        lng,
        supportItems,
        token,
    ]);
    useEffect(() => {
        // If we have a suiteKey, load the suite and find the current product. Otherwise, load the product.
        // If a product is not found, throw an exception
        if (!availableProducts || activeSuite !== suiteKey) {
            setIsLoading(true);
            const loadProducts = () => __awaiter(void 0, void 0, void 0, function* () {
                const api = getApi(environment, token);
                // TODO: handle API errors
                const suite = (yield api
                    .get(`/suites/${suiteKey}`)
                    .json());
                if (suite.supportItems) {
                    const config = {
                        fallbackLng: 'en',
                        interpolation: {
                            escapeValue: false,
                        },
                        resources: ['en', 'es', 'fr', 'pt'].reduce((res, l) => {
                            var _a;
                            const translation = (_a = suite.supportItems) === null || _a === void 0 ? void 0 : _a.reduce((tlns, si) => {
                                var _a;
                                const textValues = (_a = si.locales[l]) !== null && _a !== void 0 ? _a : {
                                    additionalText: si.additionalText,
                                    linkText: si.linkText,
                                };
                                return Object.assign(Object.assign({}, tlns), { [generateSupportItemKey(si.url)]: textValues });
                            }, {});
                            if (translation) {
                                res[l] = { translation };
                            }
                            return res;
                        }, {}),
                    };
                    const i18New = createInstance(config);
                    i18New.init();
                    i18New.changeLanguage(lng);
                    setI18n(i18New);
                }
                const product = suite.products.find((p) => p.id === productKey);
                if (!product) {
                    throw new Error('Product not found');
                }
                setAvailableProducts(suite.products);
                setSupportItems(suite.supportItems);
                setActiveProduct(product);
                setActiveSuite(suiteKey);
                setIsLoading(false);
                if (onLoadingComplete) {
                    onLoadingComplete();
                }
            });
            loadProducts();
        }
        else if ((activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.id) !== productKey && availableProducts) {
            const product = availableProducts.find((p) => p.id === productKey);
            if (!product) {
                throw new Error('Product not found');
            }
            setActiveProduct(product);
            setIsLoading(false);
            if (onLoadingComplete) {
                onLoadingComplete();
            }
        }
    }, [
        activeProduct,
        activeSuite,
        availableProducts,
        environment,
        lng,
        onLoadingComplete,
        productKey,
        suiteKey,
        token,
    ]);
    useEffect(() => {
        if (i18nInstance) {
            const currentLng = i18nInstance.language;
            if (currentLng !== lng) {
                i18nInstance.changeLanguage(lng);
            }
        }
    }, [i18nInstance, lng]);
    return (_jsx(PlatformContext.Provider, { value: context, children: _jsx(SovosProvider, { assetHost: assetHost, lng: lng, theme: merge((_a = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.theme) !== null && _a !== void 0 ? _a : {}, theme !== null && theme !== void 0 ? theme : {}), children: isLoading ? (_jsx(SovosBackdrop, { open: isLoading, children: _jsx(SovosCircularProgress, { "aria-label": "Progress loader" }) })) : (children) }) }));
};
export default SovosPlatformProvider;
